import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, DollarSign, AlertTriangle, Clock, Calendar, FileSignature, Mail } from "lucide-react";

export default function LeasesPage() {
  const { propertyId } = useProperty();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [residents, setResidents] = useState([]);
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState({
    unit: "", resident_id: "", owner_id: "",
    monthly_rent: 2500, security_deposit: 2500,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10),
    late_fee_amount: 50, grace_period_days: 5, due_day: 1,
    notes: "",
  });

  const load = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leases/rent-roll/summary?property_id=${propertyId}`);
      setRows(data.items || []);
      setTotals(data.totals || null);
    } catch (e) { toast.error("Failed to load rent roll"); }
    setLoading(false);
  };

  const loadPreview = async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/admin/leases/late-fees/preview?property_id=${propertyId}`);
      setPreview(data);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/LeasesPage.jsx: swallowed', e); }
  };

  const loadPeople = async () => {
    if (!propertyId) return;
    try {
      const { data: res } = await api.get(`/admin/users?role=resident&property_id=${propertyId}`);
      setResidents(res.items || []);
      const { data: own } = await api.get(`/admin/owners?property_id=${propertyId}`);
      setOwners(own.items || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('pages/admin/LeasesPage.jsx: swallowed', e); }
  };

  useEffect(() => {
    load();
    loadPreview();
    loadPeople();
  }, [propertyId]);

  const create = async () => {
    if (!form.unit || !form.resident_id || !form.monthly_rent) {
      toast.error("Unit, resident, and monthly rent are required");
      return;
    }
    try {
      await api.post("/admin/leases", {
        property_id: propertyId,
        ...form,
        owner_id: form.owner_id || null,
        monthly_rent: parseFloat(form.monthly_rent),
        security_deposit: parseFloat(form.security_deposit || 0),
        late_fee_amount: parseFloat(form.late_fee_amount || 0),
        grace_period_days: parseInt(form.grace_period_days || 5),
        due_day: parseInt(form.due_day || 1),
      });
      toast.success("Lease created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create lease");
    }
  };

  const signLease = async (leaseId, unit) => {
    if (!window.confirm(`Sign lease for unit ${unit || "?"} and auto-send welcome email to the resident?`)) return;
    try {
      const { data } = await api.post(`/admin/leases/${leaseId}/sign`);
      if (data.already_signed) {
        toast.info("Lease already signed — no email re-sent");
      } else if (data.invite_delivered) {
        toast.success(`Lease active! Welcome email sent to ${data.resident_email}`);
      } else {
        toast.warning(`Lease signed but welcome email could not be sent: ${data.invite_error || "unknown"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign lease");
    }
  };

  const sendForSignature = async (leaseId, unit) => {
    if (!window.confirm(`Send lease for unit ${unit || "?"} to the resident for DocuSign countersign?`)) return;
    try {
      const { data } = await api.post(`/admin/leases/${leaseId}/send-for-signature`);
      if (data.already_pending) {
        toast.info("Envelope already sent — waiting on resident");
      } else {
        toast.success(`Envelope sent to ${data.signer_email} — status now pending signature`);
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "docusign_not_configured") {
        toast.error(`DocuSign isn't configured yet · missing ${detail.missing.join(", ")}`);
      } else {
        toast.error(detail?.message || detail || "Failed to send for signature");
      }
    }
  };

  const kpis = useMemo(() => {
    if (!totals) return [];
    return [
      { label: "Active Units", value: totals.occupied_count, testid: "kpi-units" },
      { label: "Monthly Gross", value: `$${(totals.monthly_gross || 0).toLocaleString()}`, testid: "kpi-monthly" },
      { label: "Annual Gross", value: `$${(totals.annual_gross || 0).toLocaleString()}`, testid: "kpi-annual" },
      { label: "Past Due", value: totals.past_due_count, sub: `$${(totals.past_due_amount || 0).toLocaleString()}`, testid: "kpi-pastdue" },
      { label: "Deposits Held", value: `$${(totals.total_deposits_held || 0).toLocaleString()}`, testid: "kpi-deposits" },
      { label: "Renewals in 90d", value: totals.renewals_in_90d, testid: "kpi-renewals" },
    ];
  }, [totals]);

  return (
    <div className="space-y-6" data-testid="leases-page">
      <PageHeader
        eyebrow="Leasing"
        title="Rent Roll & Leases"
        subtitle="Every unit's lease, rent, deposit, and past-due balance — one grid."
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-lease-btn" className="gap-2">
                <Plus size={14} /> New lease
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="lease-dialog">
              <DialogHeader><DialogTitle>New lease</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="lease-unit" />
                </div>
                <div className="space-y-2">
                  <Label>Monthly rent</Label>
                  <Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="lease-rent" />
                </div>
                <div className="space-y-2">
                  <Label>Resident</Label>
                  <Select value={form.resident_id} onValueChange={(v) => setForm({ ...form, resident_id: v })}>
                    <SelectTrigger data-testid="lease-resident"><SelectValue placeholder="Select resident" /></SelectTrigger>
                    <SelectContent>
                      {residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner (optional)</Label>
                  <Select value={form.owner_id || "__none__"} onValueChange={(v) => setForm({ ...form, owner_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="lease-owner"><SelectValue placeholder="No owner (PM-owned)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Security deposit</Label>
                  <Input type="number" value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })} data-testid="lease-deposit" />
                </div>
                <div className="space-y-2">
                  <Label>Due day (1-28)</Label>
                  <Input type="number" min="1" max="28" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="lease-due-day" />
                </div>
                <div className="space-y-2">
                  <Label>Late fee (fixed $)</Label>
                  <Input type="number" value={form.late_fee_amount} onChange={(e) => setForm({ ...form, late_fee_amount: e.target.value })} data-testid="lease-latefee" />
                </div>
                <div className="space-y-2">
                  <Label>Grace period (days)</Label>
                  <Input type="number" min="0" max="30" value={form.grace_period_days} onChange={(e) => setForm({ ...form, grace_period_days: e.target.value })} data-testid="lease-grace" />
                </div>
                <div className="space-y-2">
                  <Label>Start date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="lease-start" />
                </div>
                <div className="space-y-2">
                  <Label>End date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="lease-end" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lease-notes" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="lease-save">Save lease</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="lease-kpis">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4 border-white/10 bg-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
            <div className="text-2xl font-semibold mt-1" data-testid={k.testid}>{k.value}</div>
            {k.sub && <div className="text-xs text-rose-400 mt-1">{k.sub}</div>}
          </Card>
        ))}
      </div>

      {preview && preview.count > 0 && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="lease-latefee-preview">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 mt-1" size={18} />
            <div className="flex-1">
              <div className="font-semibold">Late fees will post automatically</div>
              <div className="text-sm text-muted-foreground mt-1">
                <span data-testid="latefee-preview-count">{preview.count}</span> rent charge{preview.count === 1 ? "" : "s"} are past grace period.
                Estimated fees: <strong>${(preview.total_fees || 0).toLocaleString()}</strong>. The hourly late-fee generator will post these automatically.
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10 flex items-center gap-2">
          <DollarSign size={12} /> Rent Roll · {loading ? "loading…" : `${rows.length} lease${rows.length === 1 ? "" : "s"}`}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Unit</TableHead>
              <TableHead>Resident</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Rent</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Lease Ends</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow className="border-white/5"><TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-sm" data-testid="rentroll-empty">
                No active leases. Click <em>New lease</em> to add the first one.
              </TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.lease_id} className="border-white/5" data-testid={`rentroll-row-${r.lease_id}`}>
                <TableCell className="font-medium">{r.unit || "—"}</TableCell>
                <TableCell>
                  <div>{r.resident_name || <span className="text-muted-foreground italic">unassigned</span>}</div>
                  <div className="text-xs text-muted-foreground">{r.resident_email}</div>
                </TableCell>
                <TableCell className="text-sm">{r.owner_name || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right font-mono">${(r.monthly_rent || 0).toLocaleString()}</TableCell>
                <TableCell className={`text-right font-mono ${r.past_due ? "text-rose-400" : ""}`}>
                  ${(r.balance || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar size={12} className="text-muted-foreground" />
                    {r.end_date || "—"}
                  </div>
                  {r.days_to_end !== null && r.days_to_end >= 0 && r.days_to_end <= 90 && (
                    <Badge variant="secondary" className="mt-1 text-[10px]">
                      <Clock size={10} className="mr-1" />
                      {r.days_to_end}d
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={r.past_due ? "destructive" : "default"}>
                    {r.past_due ? "past due" : r.renewal_status || "active"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {r.movein_batch_id ? (
                    <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-300"
                            data-testid={`rentroll-signed-${r.lease_id}`}>
                      signed
                    </Badge>
                  ) : r.docusign_envelope_id ? (
                    <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300"
                            data-testid={`rentroll-pending-${r.lease_id}`}>
                      pending signature
                    </Badge>
                  ) : (
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs border-orange-500/30 text-orange-300 bg-orange-500/5 hover:bg-orange-500/10"
                        onClick={() => signLease(r.lease_id, r.unit)}
                        data-testid={`sign-lease-${r.lease_id}`}
                      >
                        <FileSignature size={11} /> Sign & Invite
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs border-sky-500/30 text-sky-300 bg-sky-500/5 hover:bg-sky-500/10"
                        onClick={() => sendForSignature(r.lease_id, r.unit)}
                        data-testid={`docusign-lease-${r.lease_id}`}
                      >
                        <Mail size={11} /> DocuSign
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
